/**
 * G5 agent-led project setup (docs/PLAN.md G5 / V25): New project → "Scope with an agent".
 *
 * 1. `startScoping`: the encrypted account project exists first (name, goal, remote or null),
 *    then the chosen machine opens a scoping workspace (`project.scope`, device-signed; it clones
 *    and creates nothing) and a discuss bark starts the conversation there. The bark IS the saved
 *    scoping conversation; research the agent saves lands in the project's library.
 * 2. `buildSetupCards`: the sealed conversation + the machine's scope result + the learned
 *    tendencies → reviewable proposal cards (goal and milestones, the shared config with every
 *    choice explained, lanes, first tasks, and normalizations for an existing repo).
 * 3. `acceptSetup`: ONE passkey checks the project out on the chosen machines and the config
 *    author commits exactly the reviewed config; then the goal, lanes and tasks are written as
 *    encrypted entities. Normalizations become tasks that land through verification (or the v2
 *    migration card); nothing edits user-owned global files.
 */
import {
  type BarkRecord,
  deriveSetupOutcome,
  type GoalMilestone,
  type ProjectScopePayload,
  ProjectScopeResult,
  type ProjectSetupState,
  type ProjectTendencies,
  type SetupEvidence,
  type SetupFinding,
} from '@viko/protocol'
import {
  type CheckoutDeps,
  type CheckoutProgress,
  checkoutOk,
  type RepoChoice,
  reportedRemote,
  requestCheckouts,
  slugify,
} from './account-projects.js'
import { applyTendencies, type ConfigChoice, tendencyLines } from './tendencies.js'
import type { CommandOutcome, Project } from './types.js'

export type SetupCard =
  | {
      id: 'goal'
      kind: 'goal'
      goal: string
      milestones: { title: string; criteria: string[] }[]
      evidence: SetupEvidence[]
    }
  | { id: 'config'; kind: 'config'; yaml: string; choices: ConfigChoice[] }
  | { id: string; kind: 'lane'; name: string; brief?: string; evidence: SetupEvidence[] }
  | { id: string; kind: 'task'; title: string; lane?: string; evidence: SetupEvidence[] }
  | { id: string; kind: 'normalize'; finding: SetupFinding }

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

/** The repo choice as the scope payload carries it. */
export function scopeRepo(repo: RepoChoice): ProjectScopePayload['repo'] {
  return repo.kind === 'create'
    ? {
        kind: 'create',
        target: { provider: 'github', owner: repo.owner, name: repo.name, visibility: 'private' },
      }
    : { kind: 'existing', remote: repo.remote.trim() }
}
export function repoChoiceOf(r: ProjectScopePayload['repo']): RepoChoice {
  return r.kind === 'create'
    ? { kind: 'create', owner: r.target.owner, name: r.target.name }
    : { kind: 'existing', remote: r.remote }
}

/** The opening message (no "Goal:" line: the typed goal is context, the agent restates it). */
export function openingMessage(name: string, goal: string): string {
  return [
    `Help me scope the new project "${name}".`,
    goal.trim() ? `What I want: ${goal.trim()}` : 'I have not written a goal yet.',
    'Propose the goal, milestones, lanes, first tasks and any config changes.',
  ].join('\n\n')
}

export interface ScopingDeps {
  createAccountProject(body: {
    name: string
    goal?: string
    remote: string | null
  }): Promise<Project>
  updateSetup(projectId: string, setup: ProjectSetupState): Promise<unknown>
  dispatchScope(machineId: string, payload: ProjectScopePayload): Promise<CommandOutcome>
  submitBark(i: {
    projectId: string
    machineId: string
    text: string
  }): Promise<Pick<BarkRecord, 'id'>>
  now(): number
}

export async function startScoping(
  deps: ScopingDeps,
  i: {
    name: string
    goal: string
    repo: RepoChoice
    machineId: string
    tendencies?: ProjectTendencies
    /** Retry on an already created project. */
    projectId?: string
  },
): Promise<{
  projectId: string
  barkId: string
  scope: ProjectScopeResult
  setup: ProjectSetupState
}> {
  const name = i.name.trim()
  const projectId =
    i.projectId ??
    (
      await deps.createAccountProject({
        name,
        ...(i.goal.trim() ? { goal: i.goal.trim() } : {}),
        remote: i.repo.kind === 'existing' ? i.repo.remote.trim() : null,
      })
    ).id
  const dirName = slugify(i.repo.kind === 'create' ? i.repo.name : name)
  const repo = scopeRepo(i.repo)
  const out = await deps.dispatchScope(i.machineId, {
    projectId,
    name,
    ...(i.goal.trim() ? { goal: i.goal.trim().slice(0, 4000) } : {}),
    repo,
    dirName,
    ...(i.tendencies?.items.length ? { tendencies: tendencyLines(i.tendencies) } : {}),
  })
  const scope = ProjectScopeResult.parse(out.result)
  if (scope.status !== 'ready')
    throw new Error(scope.reason ?? 'The machine could not open a scoping workspace.')
  const bark = await deps.submitBark({
    projectId,
    machineId: i.machineId,
    text: openingMessage(name, i.goal),
  })
  const setup: ProjectSetupState = {
    state: 'scoping',
    barkId: bark.id,
    machineId: i.machineId,
    startedAt: deps.now(),
    repo,
    dirName,
    scope,
  }
  await deps.updateSetup(projectId, setup)
  return { projectId, barkId: bark.id, scope, setup }
}

/** Every card the conversation and the scope result support right now (deterministic ids). */
export function buildSetupCards(i: {
  bark?: Pick<BarkRecord, 'thread'>
  scope?: ProjectScopeResult
  tendencies?: ProjectTendencies
  typedGoal: string
  existing: boolean
}): SetupCard[] {
  const o = i.bark ? deriveSetupOutcome(i.bark) : undefined
  const cards: SetupCard[] = []
  const goal = o?.goal?.text ?? i.typedGoal.trim()
  if (goal)
    cards.push({
      id: 'goal',
      kind: 'goal',
      goal,
      milestones: (o?.milestones ?? []).map((m) => ({ title: m.title, criteria: m.criteria })),
      evidence: [o?.goal?.evidence, ...(o?.milestones ?? []).map((m) => m.evidence)].filter(
        (e): e is SetupEvidence => !!e,
      ),
    })
  if (i.scope?.draftConfig) {
    const r = applyTendencies(i.scope.draftConfig, i.tendencies, {
      existing: i.existing,
      agent: (o?.config ?? []).map((c) => ({
        key: c.key,
        value: c.value,
        why: `Proposed in the conversation: “${c.evidence.quote}”`,
      })),
    })
    cards.push({ id: 'config', kind: 'config', yaml: r.yaml, choices: r.choices })
  }
  for (const l of o?.lanes ?? [])
    cards.push({
      id: `lane:${slugify(l.name)}`,
      kind: 'lane',
      name: l.name,
      ...(l.brief ? { brief: l.brief } : {}),
      evidence: [l.evidence],
    })
  for (const t of o?.tasks ?? [])
    cards.push({
      id: `task:${slugify(t.title)}`,
      kind: 'task',
      title: t.title,
      ...(t.lane ? { lane: t.lane } : {}),
      evidence: [t.evidence],
    })
  for (const f of i.scope?.findings ?? [])
    cards.push({ id: `fix:${f.id}`, kind: 'normalize', finding: f })
  return cards
}

export interface AcceptDeps extends CheckoutDeps {
  recordCheckouts(
    projectId: string,
    body: { machineIds: string[]; statementId?: string; via: 'checkout'; remote?: string },
  ): Promise<unknown>
  saveGoal(projectId: string, goal: { text: string; milestones: GoalMilestone[] }): Promise<unknown>
  createWorkstream(i: { projectId: string; name: string; brief?: string }): Promise<{ id: string }>
  createTask(i: {
    projectId: string
    title: string
    workstreamId?: string
    description?: string
  }): Promise<{ id: string }>
  updateSetup(projectId: string, setup: ProjectSetupState): Promise<unknown>
}

export interface AcceptResult {
  checkouts: CheckoutProgress[]
  goal: boolean
  lanes: string[]
  tasks: string[]
  /** Normalizations that need the existing repo's v2 migration card. */
  migrate: boolean
}

const quote = (e: SetupEvidence[]) =>
  e[0] ? `\n\nFrom the scoping conversation: “${e[0].quote}”` : ''

/**
 * Create the project from the accepted cards. The scoping machine is the config author (first
 * machine), so the reviewed config it scoped is what gets committed.
 */
export async function acceptSetup(
  deps: AcceptDeps,
  i: {
    projectId: string
    name: string
    typedGoal: string
    repo: RepoChoice
    machineIds: string[]
    authorMachineId: string
    cards: SetupCard[]
    setup: ProjectSetupState
  },
  onProgress: (p: CheckoutProgress) => void = () => {},
): Promise<AcceptResult> {
  const machineIds = [i.authorMachineId, ...i.machineIds.filter((m) => m !== i.authorMachineId)]
  const config = i.cards.find((c) => c.kind === 'config')
  const goalCard = i.cards.find((c) => c.kind === 'goal')
  const { statement, results } = await requestCheckouts(
    deps,
    {
      projectId: i.projectId,
      machineIds,
      repo: i.repo,
      dirName: i.setup.dirName ?? slugify(i.repo.kind === 'create' ? i.repo.name : i.name),
      name: i.name,
      ...(goalCard?.kind === 'goal'
        ? { goal: goalCard.goal }
        : i.typedGoal
          ? { goal: i.typedGoal }
          : {}),
      author: true,
      ...(config?.kind === 'config' ? { config: config.yaml } : {}),
    },
    onProgress,
  )
  const remote = reportedRemote(results)
  await deps.recordCheckouts(i.projectId, {
    machineIds,
    statementId: statement.statementId,
    via: 'checkout',
    ...(remote ? { remote: `https://${remote}` } : {}),
  })
  const out: AcceptResult = {
    checkouts: results,
    goal: false,
    lanes: [],
    tasks: [],
    migrate: false,
  }
  // Nothing else is written until the project exists on at least one machine.
  if (!results.some(checkoutOk)) return out

  if (goalCard?.kind === 'goal') {
    await deps.saveGoal(i.projectId, {
      text: goalCard.goal,
      milestones: goalCard.milestones.map((m, n) => ({
        id: `m${n + 1}`,
        title: m.title,
        criteria: m.criteria.map((text, k) => ({ id: `m${n + 1}c${k + 1}`, text, evidence: [] })),
      })),
    })
    out.goal = true
  }
  const lanes = new Map<string, string>()
  for (const c of i.cards)
    if (c.kind === 'lane') {
      const ws = await deps.createWorkstream({
        projectId: i.projectId,
        name: c.name,
        ...(c.brief ? { brief: c.brief } : {}),
      })
      lanes.set(norm(c.name), ws.id)
      out.lanes.push(ws.id)
    }
  for (const c of i.cards) {
    if (c.kind === 'task') {
      const workstreamId = c.lane ? lanes.get(norm(c.lane)) : undefined
      const t = await deps.createTask({
        projectId: i.projectId,
        title: c.title,
        ...(workstreamId ? { workstreamId } : {}),
        description: `Scoped with Viko.${quote(c.evidence)}`,
      })
      out.tasks.push(t.id)
    } else if (c.kind === 'normalize') {
      if (c.finding.via === 'migrate-config') {
        out.migrate = true
        continue
      }
      const f = c.finding
      const t = await deps.createTask({
        projectId: i.projectId,
        title: f.title,
        description: [
          f.detail,
          f.paths.length ? `Files (in this repository only): ${f.paths.join(', ')}` : '',
          'Do not edit files outside the repository (global CLAUDE.md/AGENTS.md, ~/.claude, ~/.codex).',
          f.patch ? `Proposed change:\n\n\`\`\`diff\n${f.patch}\n\`\`\`` : '',
        ]
          .filter(Boolean)
          .join('\n\n'),
      })
      out.tasks.push(t.id)
    }
  }
  if (out.migrate) {
    const author = results.find((r) => r.machineId === i.authorMachineId && checkoutOk(r))
    if (author) {
      await deps.dispatch('project.migrate_config', i.authorMachineId, { projectId: i.projectId })
      out.migrate = false
    }
  }
  await deps.updateSetup(i.projectId, {
    ...i.setup,
    state: 'created',
    createdAt: deps.now(),
    accepted: i.cards.map((c) => c.id).slice(0, 128),
  })
  return out
}
